/**
 * GitHubConfigCard - GitHub repository configuration card
 *
 * Features:
 * - Display list of configured GitHub repositories
 * - Add new repositories
 * - Edit default branch
 * - Remove repositories
 */

import { createPortal } from 'react-dom';
import {  Plus, Trash2, X } from 'lucide-react';
import type { GitHubIntegrationState } from '@resolve-ai/shared';
import type { Repository } from '@/lib/integrations-api.ts';
import { StatusBadge, Card, ConfirmDialog } from '../common';
import {EditIcon, GitBranchIcon, GitHubLogo} from '../icons';

interface GitHubConfig {
  id: string;
  repoOwner: string;
  repoName: string;
  repoFullName: string;
  defaultBranch: string;
}

interface GitHubConfigCardProps {
  githubConfigs: GitHubConfig[];
  githubIntegrationStatus: GitHubIntegrationState | null;
  isAdmin: boolean;
  isLoadingRepos: boolean;
  showAddRepoModal: boolean;
  availableRepos: Repository[];
  selectedRepoToAdd: Repository | null;
  repoToDelete: { id: string; name: string } | null;
  onOpenAddRepoModal: () => void;
  onCloseAddRepoModal: () => void;
  onSelectRepo: (repo: Repository | null) => void;
  onAddRepo: () => void;
  onDeleteRepo: () => Promise<void>;
  onSetRepoToDelete: (repo: { id: string; name: string } | null) => void;
  onEditBranch: (config: {
    id: string;
    repoOwner: string;
    repoName: string;
    repoFullName: string;
    defaultBranch: string;
  }) => void;
}

export function GitHubConfigCard({
  githubConfigs,
  githubIntegrationStatus,
  isAdmin,
  isLoadingRepos,
  showAddRepoModal,
  availableRepos,
  selectedRepoToAdd,
  repoToDelete,
  onOpenAddRepoModal,
  onCloseAddRepoModal,
  onSelectRepo,
  onAddRepo,
  onDeleteRepo,
  onSetRepoToDelete,
  onEditBranch,
}: GitHubConfigCardProps) {
  return (
    <>
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <GitHubLogo className="w-4 h-4 text-gray-900 dark:text-white" />
            <h3 className="text-[1rem] leading-[140%] font-semibold text-gray-900 dark:text-white">
              GitHub Repositories
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {githubIntegrationStatus?.status && <StatusBadge status={githubIntegrationStatus.status} size="lg" />}
            {isAdmin && (
              <button
                onClick={onOpenAddRepoModal}
                disabled={isLoadingRepos}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            )}
          </div>
        </div>
        {githubConfigs.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">No repositories configured</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {githubConfigs.map((config) => (
              <div
                key={config.id}
                className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center justify-between gap-3"
              >
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {config.repoFullName}
                  </p>
                  <div className="flex items-center  mt-1">
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-md">
                      <GitBranchIcon className="w-3 h-3" />
                      {config.defaultBranch}
                    </span>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-4 items-center text-gray-600 dark:text-gray-400">
                    <button
                      onClick={() => onEditBranch(config)}
                      className=" hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Edit default branch"
                    >
                      <EditIcon />
                    </button>
                    {githubConfigs.length > 1 && (
                      <button
                        onClick={() => onSetRepoToDelete({ id: config.id, name: config.repoFullName })}
                        className=" hover:text-red-500 rounded-lg transition-colors"
                        title="Remove repository"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add Repository Modal */}
      {showAddRepoModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Add Repository
              </h3>
              <button
                onClick={onCloseAddRepoModal}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {availableRepos.length === 0 ? (
                <p className="text-center text-gray-600 dark:text-gray-400 py-8">
                  No additional repositories available
                </p>
              ) : (
                <div className="space-y-2">
                  {availableRepos.map((repo) => (
                    <label
                      key={repo.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${selectedRepoToAdd?.id === repo.id
                        ? 'bg-indigo-50 dark:bg-indigo-900/30 border-2 border-indigo-500'
                        : 'bg-gray-50 dark:bg-gray-700/50 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                    >
                      <input
                        type="radio"
                        name="repo"
                        checked={selectedRepoToAdd?.id === repo.id}
                        onChange={() => onSelectRepo(repo)}
                        className="sr-only"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {repo.full_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Default branch: {repo.default_branch}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-0.5 text-xs rounded ${repo.private
                          ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400'
                          : 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                          }`}
                      >
                        {repo.private ? 'Private' : 'Public'}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onCloseAddRepoModal}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onAddRepo}
                disabled={!selectedRepoToAdd}
                className={`px-4 py-2 rounded-lg transition-colors ${selectedRepoToAdd
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                  }`}
              >
                Add Repository
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Repository Confirmation */}
      <ConfirmDialog
        isOpen={!!repoToDelete}
        title="Remove Repository"
        message={`Are you sure you want to remove "${repoToDelete?.name}" from this project? This action cannot be undone.`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={onDeleteRepo}
        onClose={() => onSetRepoToDelete(null)}
      />
    </>
  );
}
